import {
    type ViewStyle,
    StyleSheet
} from "react-native";
import type {
    BubbleMarkdownVariantsConstantType,
    BubbleContentDynamicStyleType
} from "./type";
import type {
    Mutable
} from "../../../../types";
import type {
    MarkdownVariantsType
} from "../../../markdownViewer/type";
import type {
    BubbleGroupPosition,
    BubbleAlign
} from "../../type";
import {
    getFocusRingStyle
} from "../../../../helpers/theme/state";
import {
    getCornerStyle
} from "../../../../helpers/theme/corner";
import {
    getHitArea
} from "../../../../helpers/theme/control";
import {
    webStyle
} from "../../../../utils";

export const BUBBLE_JOIN_RADIUS_KEYS: Record<BubbleAlign, Record<"bottom" | "top", keyof ViewStyle>> = {
    start: {
        bottom: "borderEndStartRadius",
        top: "borderStartStartRadius"
    },
    end: {
        bottom: "borderEndEndRadius",
        top: "borderStartEndRadius"
    }
};

export const BUBBLE_JOINED_EDGES: Record<BubbleGroupPosition, Array<"bottom" | "top">> = {
    first: [
        "bottom"
    ],
    middle: [
        "top",
        "bottom"
    ],
    single: [],
    last: [
        "top"
    ]
};

export const getBubbleMarkdownVariants = ({
    typography
}: BubbleMarkdownVariantsConstantType): MarkdownVariantsType => {
    return {
        "###### ": "titleSmallSize",
        "##### ": "titleSmallSize",
        "#### ": "titleSmallSize",
        "## ": "titleMediumSize",
        "### ": "titleSmallSize",
        "# ": "titleLargeSize",
        "<br/>": typography,
        "<p>": typography,
        "1. ": typography,
        "- ": typography,
        "* ": typography,
        "+ ": typography,
        "<<": typography,
        "> ": typography,
        "": typography
    };
};

const stylesheet = StyleSheet.create({
    surface: {
        flexDirection: "column",
        borderStyle: "solid",
        maxWidth: "100%",
        minWidth: 0,
        ...webStyle({
            overflowWrap: "anywhere"
        })
    },
    pressable: {
        justifyContent: "center",
        flexShrink: 1,
        minWidth: 0,
        ...webStyle({
            outlineStyle: "solid",
            outlineWidth: 0
        })
    },
    user: {
        writingDirection: "auto"
    },
    fade: {
        pointerEvents: "none",
        position: "absolute",
        bottom: 0,
        start: 0,
        end: 0
    },
    clip: {
        overflow: "hidden"
    },
    measure: {
        flexShrink: 0
    },
    text: {
        flexShrink: 1
    }
});

export const useStyles = ({
    controlPaddings,
    controlHeights,
    isFocusVisible,
    currentColors,
    groupPosition,
    reactionsSide,
    hasReactions,
    shapeConfigs,
    touchTargets,
    currentSize,
    isPressable,
    pointerType,
    groupAlign,
    isClipped,
    maxHeight,
    radiuses,
    borders,
    variant,
    colors,
    spaces
}: BubbleContentDynamicStyleType) => {
    const isPlain = variant === "plain";

    const paddingHorizontal = isPlain ? 0 : currentSize.paddingHorizontal;

    const hitArea = getHitArea({
        visualSize: currentSize.minHeight,
        touchTargets,
        pointerType
    });

    const styles = {
        surface: {
            ...getCornerStyle({
                radius: isPlain ? radiuses.xs : isPressable ? radiuses.chip : radiuses.surface,
                shapeConfigs
            }),
            backgroundColor: currentColors.containerColor,
            borderColor: currentColors.borderColor,
            gap: spaces.spacingMd
        } as Mutable<ViewStyle>,
        toggle: {
            marginStart: paddingHorizontal - controlPaddings.small,
            marginTop: spaces.spacingXxs
        } as Mutable<ViewStyle>,
        pressable: {
            minHeight: hitArea.minHeight
        } as Mutable<ViewStyle>,
        fade: {
            height: spaces.spacingXl
        } as Mutable<ViewStyle>,
        clip: {
        } as Mutable<ViewStyle>
    };

    if(!isPlain) {
        Object.assign(styles.surface, {
            paddingVertical: (currentSize.minHeight - currentSize.lineHeight) / 2 - borders.line,
            paddingHorizontal: paddingHorizontal - borders.line,
            minHeight: currentSize.minHeight,
            borderWidth: borders.line
        });
    }

    if(hasReactions) {
        Object.assign(styles.surface, {
            [reactionsSide === "bottom" ? "paddingBottom" : "paddingTop"]: controlHeights.small / 2
        });
    }

    if(!isPlain && groupPosition && groupAlign) {
        BUBBLE_JOINED_EDGES[groupPosition].forEach((edge) => {
            Object.assign(styles.surface, {
                [BUBBLE_JOIN_RADIUS_KEYS[groupAlign][edge]]: radiuses.xs
            });
        });
    }

    if(isClipped) {
        styles.clip.maxHeight = maxHeight;
    }

    Object.assign(styles.surface, getFocusRingStyle({
        isFocusVisible,
        borders,
        colors
    }));

    if(!isPressable) {
        delete styles.pressable.minHeight;
    }

    return styles;
};
export default stylesheet;
